import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import TrustPilotStars from "components/TrustPilotStars";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import PaymentButton from "./PaymentButton";
import PaymentMethods from "./PaymentMethods";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import { SelectablePaymentMethodEnum } from "config/site";
import { FC, useEffect, useState } from "react";
import { useWebTranslation } from "translations";
import { PaymentError } from "./types";
import styles from "./PaymentView.module.css";
import InnerLayout from "components/InnerLayout/InnerLayout";
import ResponsiveGutter from "ui-kit/ResponsiveGutter";
import { PaymentMethodState } from "./usePaymentMethodState";
import {
  useBillingDay,
  useBillingInterval,
} from "components/Signup/store/selectors/animals";
import { SummaryStatement } from "../SummaryStatement";
import { Benefits } from "./Benefits";
import { SavingsBanner } from "../SavingsBanner";
import { isOrg } from "utils/site";
import { TuvBadges } from "components/TuvBadges";
import { useCustomer } from "components/Signup/store/selectors/customer";
import LassieLoader from "ui-kit/LassieLoader";
import ChoosePaymentInterval from "../ChoosePaymentInterval";
import {
  updateBillingDay,
  updateBillingInterval,
} from "components/Signup/store/slices/animals";
import { useSignupDispatch } from "components/Signup/store/hooks";
import ChoosePaymentDay from "../ChoosePaymentDay";
import { Experiment } from "components/Experiment";
import PaymentInfo from "./PaymentInfo";

type PaymentViewProps = {
  selectablePaymentMethods: SelectablePaymentMethodEnum[];
  paymentMethod: SelectablePaymentMethodEnum;
  setPaymentMethod: (paymentMethod: SelectablePaymentMethodEnum) => void;
  paymentMethodState: PaymentMethodState;
  setPaymentMethodState: ({ isValid, data }: PaymentMethodState) => void;
  loading?: boolean;
  initiated?: boolean;
  error: PaymentError | null;
  onPaymentClick: () => Promise<void>;
};

const PaymentView: FC<PaymentViewProps> = ({
  selectablePaymentMethods,
  paymentMethod,
  setPaymentMethod,
  paymentMethodState,
  setPaymentMethodState,
  loading,
  initiated,
  error,
  onPaymentClick,
}) => {
  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    setTimeout(() => {
      setOpacity(1);
    }, 200);
  }, []);
  const { t } = useWebTranslation();
  const billingInterval = useBillingInterval();
  const billingDay = useBillingDay();
  const customer = useCustomer();
  const dispatch = useSignupDispatch();

  return (
    <div style={{ opacity, transition: "250ms ease-in" }}>
      {initiated ? (
        <>
          <InnerLayout>
            <Gutter size={3} />
            <FlexCol alignItems="center">
              <Typography variant="h2">{t("payment.title")}</Typography>
            </FlexCol>
            <Gutter size={3} />
            <FlexRow className={styles.content}>
              <div className={styles.leftColumn}>
                <div className={styles.summary}>
                  <Typography variant="body2" color="grey500">
                    <strong>{t("insurance.summary.title")}</strong>
                  </Typography>
                  <SavingsBanner />
                  <SummaryStatement />
                  {isOrg("fra") && (
                    <FlexCol>
                      <Gutter size={2} />
                      <FlexRow justifyContent="center">
                        <TrustPilotStars fullWidth />
                      </FlexRow>
                    </FlexCol>
                  )}
                  {isOrg("ger") && (
                    <FlexCol>
                      <Gutter size={2} />
                      <FlexRow justifyContent="center">
                        <TrustPilotStars fullWidth />
                      </FlexRow>
                      <Gutter size={4} />
                      <FlexRow justifyContent="center">
                        <TuvBadges />
                      </FlexRow>
                    </FlexCol>
                  )}
                  <Benefits />
                </div>
              </div>
              <div className={styles.rightColumn}>
                <ResponsiveGutter mobile={0} tablet={2} desktop={2} />
                <PaymentMethods
                  billingDetails={{
                    name: `${customer.firstName} ${customer.lastName}`,
                    email: customer.email ?? "",
                    phone: customer.phone ?? "",
                  }}
                  selectablePaymentMethods={selectablePaymentMethods}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  paymentMethodState={paymentMethodState}
                  setPaymentMethodState={setPaymentMethodState}
                />
                <Gutter size={4} />
                <ChoosePaymentInterval
                  value={billingInterval}
                  onChange={(billingInterval) =>
                    dispatch(updateBillingInterval(billingInterval))
                  }
                  smallTitle
                />
                <Gutter size={4} />
                <Experiment
                  featureName="choose-payment-day"
                  variations={{
                    control: <PaymentInfo billingInterval={billingInterval} />,
                    treatment: (
                      <ChoosePaymentDay
                        value={billingDay}
                        onChange={(billingDay) =>
                          dispatch(updateBillingDay(billingDay))
                        }
                        billingInterval={billingInterval}
                        smallTitle
                      />
                    ),
                  }}
                />
                {error && (
                  <>
                    <Gutter size={4} />
                    <ErrorMessage>
                      <Typography color="error">
                        {t("payment.error")}
                      </Typography>
                    </ErrorMessage>
                  </>
                )}
                <Gutter size={4} />
                <PaymentButton
                  loading={loading}
                  disabled={!paymentMethodState.isValid}
                  onPaymentClick={onPaymentClick}
                  paymentMethod={paymentMethod}
                  className={styles.desktop}
                />
              </div>
            </FlexRow>
            <Gutter size={4} />
            <PaymentButton
              loading={loading}
              disabled={!paymentMethodState.isValid}
              onPaymentClick={onPaymentClick}
              paymentMethod={paymentMethod}
              className={styles.mobile}
            />
          </InnerLayout>
        </>
      ) : (
        <FlexRow
          justifyContent="center"
          alignItems="center"
          style={{
            minHeight: "calc(100vh - var(--signup-header-height) - 20px)",
          }}
        >
          <LassieLoader />
        </FlexRow>
      )}
    </div>
  );
};

export default PaymentView;
